import jsPDF from 'jspdf';
import moment from 'moment';
import templatePage1 from './assets/contract_template_p1.jpg';
import templatePage2 from './assets/contract_template_p2.jpg';
import templatePage3 from './assets/contract_template_p3.jpg';
import templatePage4 from './assets/contract_template_p4.jpg';

// Fills the "Employment Contract (Bilingual)" PDF template with jsPDF (the same library the other PDFs use).
// jsPDF cannot open an existing PDF, so the 4 template pages (Employment_Contract_Template.pdf from the server,
// 612 x 792 pt Letter) are stored as page images in assets/contract_template_p1..p4.jpg (150 dpi) and used
// as the page backgrounds. Each value is then placed into its table cell by position (coordinates below are
// in points from the top-left of the page) and drawn as a small text image so any script (including Tamil)
// renders correctly. The field mapping and value formatting are the same as the Word template filler
// (EmploymentContractTemplate.js). If the template PDF changes, re-export its pages to those 4 images and
// re-check the coordinates.

const PAGE_W = 612;
const PAGE_H = 792;
const TEMPLATE_PAGES = [templatePage1, templatePage2, templatePage3, templatePage4];
const SCALE = 4; // canvas px per pt for the text images
const FONT = '"Times New Roman", "Noto Serif Tamil", "Nirmala UI", "Latha", serif';

const clean = (value) => (value === undefined || value === null ? '' : String(value).trim());

const formatDate = (value) => {
    const text = clean(value);
    if (!text) return '';
    const parsed = moment(text, [moment.ISO_8601, 'DD/MM/YYYY', 'DD-MM-YYYY', 'DD-MMM-YYYY', 'MM/DD/YYYY'], true);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : text;
};

// Value cells: page (0-based) and [x0, y0, x1, y1] of the cell.
// clear: white-out the cell's fixed template text first (Department / Employment Type option lists, the
// Salary "₹") - an empty value then leaves the cell empty, as in the Word template. 'ifValue' (signature
// "Name:") only clears when there is a value to write.
const COL = [305.9, 565.3]; // value column of the two-column tables (x0, x1)
const cell = (page, y0, y1, extra = {}) => ({ page, box: [COL[0], y0, COL[1], y1], ...extra });
const CELLS = {
    employeeName:        cell(0, 311.1, 334.6),
    fatherOrSpouseName:  cell(0, 334.6, 363.2),
    employeeAddress:     cell(0, 363.2, 375.9),
    employeeIdNumber:    cell(0, 375.9, 392.7),
    employeeMobileNo:    cell(0, 392.7, 405.4),
    emergencyContact:    cell(0, 405.4, 417.7),
    designation:         cell(0, 448.9, 461.1),
    department:          cell(0, 461.1, 485.2, { clear: true }),
    workLocation:        cell(0, 485.2, 497.5),
    dateOfJoining:       cell(0, 497.5, 509.7),
    employmentType:      cell(0, 509.7, 521.9, { clear: true }),
    probationPeriod:     cell(0, 521.9, 542.6),
    reportingTo:         cell(0, 542.6, 554.8),
    salary:              cell(1, 151.9, 164.1, { clear: true }),
    salaryType:          cell(1, 164.1, 176.4),
    paymentType:         cell(1, 176.4, 188.6),
    dailyHours:          cell(1, 290.5, 302.8),
    breakTime:           cell(1, 302.8, 315.0),
    weeklyOff:           cell(1, 315.0, 327.2),
    noticePeriod:        cell(2, 611.2, 623.3),
    // Signatures: the Employee column's "Name:" row ("Name: <employee name>")
    signatureName:       { page: 3, box: [306.1, 366.7, 565.3, 386.1], clear: 'ifValue' },
};

// Contract date blanks ("... is made on ____" and its Tamil counterpart) on page 1
const DATE_BLANKS = [[218.8, 146.1, 294.5, 157.8], [205.4, 189.7, 281.1, 201.4]];

// Numbered Roles & Responsibilities rows (value cell of each of the 5 rows): rows 1-2 are at the bottom of
// page 1, rows 3-5 continue at the top of page 2
const RESPONSIBILITY_ROWS = [
    { page: 0, box: [73.8, 719.9, 570.7, 732.1] },
    { page: 0, box: [73.8, 732.1, 570.7, 744.5] },
    { page: 1, box: [73.8, 70.2, 570.7, 82.6] },
    { page: 1, box: [73.8, 82.6, 570.7, 94.9] },
    { page: 1, box: [73.8, 94.9, 570.7, 107.3] },
];

const PAD_X = 4;
const MAX_PT = 9;
const MIN_PT = 6;

// Wraps text to a pixel width with the canvas context's current font
const wrap = (ctx, text, widthPx) => {
    const lines = [];
    String(text).split(/\r?\n/).forEach((paragraph) => {
        let current = '';
        paragraph.split(/\s+/).forEach((word) => {
            const test = current ? `${current} ${word}` : word;
            if (current && ctx.measureText(test).width > widthPx) { lines.push(current); current = word; } else { current = test; }
        });
        lines.push(current);
    });
    return lines;
};

// Text image for a cell: the largest font (MAX_PT..MIN_PT) whose wrapped lines fit the box; if even the
// smallest does not fit, the last line ends with "…" and `truncated` is set.
const textImage = (text, box, { bold = false } = {}) => {
    if (!text) return null;
    const [x0, y0, x1, y1] = box;
    const widthPt = (x1 - x0) - PAD_X * 2;
    const heightPt = (y1 - y0) - 1;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const setFont = (size) => { ctx.font = `${bold ? 'bold ' : ''}${size * SCALE}px ${FONT}`; };

    let size = MAX_PT;
    let lines = [];
    let fits = false;
    for (; size >= MIN_PT; size -= 0.5) {
        setFont(size);
        lines = wrap(ctx, text, widthPt * SCALE);
        if (lines.length * size * 1.15 <= heightPt) { fits = true; break; }
    }
    let truncated = false;
    if (!fits) {
        size = MIN_PT;
        setFont(size);
        lines = wrap(ctx, text, widthPt * SCALE);
        const maxLines = Math.max(1, Math.floor(heightPt / (size * 1.15)));
        if (lines.length > maxLines) {
            truncated = true;
            lines = lines.slice(0, maxLines);
            let last = lines[maxLines - 1];
            while (last && ctx.measureText(`${last}…`).width > widthPt * SCALE) { last = last.slice(0, -1); }
            lines[maxLines - 1] = `${last.trimEnd()}…`;
        }
    }

    const lineH = size * 1.15 * SCALE;
    // Only as wide as the text (not the whole cell): the PDF stores every image pixel, so a full-width
    // canvas of mostly empty pixels made the file several MB larger.
    const usedWidth = lines.reduce((max, line) => Math.max(max, ctx.measureText(line).width), 0);
    canvas.width = Math.max(1, Math.min(Math.ceil(widthPt * SCALE), Math.ceil(usedWidth) + 2));
    canvas.height = Math.ceil(lines.length * lineH);
    setFont(size); // resizing the canvas resets the context
    ctx.fillStyle = '#000';
    ctx.textBaseline = 'middle';
    lines.forEach((line, i) => ctx.fillText(line, 0, (i + 0.5) * lineH));

    const hPt = canvas.height / SCALE;
    return {
        data: canvas.toDataURL('image/png'),
        x: x0 + PAD_X,
        y: y0 + ((y1 - y0) - hPt) / 2, // vertically centred in the cell
        w: canvas.width / SCALE,
        h: hPt,
        truncated,
    };
};

// Page image -> JPEG data URL. The data URL is built here from the raw bytes instead of using
// FileReader.readAsDataURL: that takes its "data:<type>;base64," prefix from the response's content type,
// and when the server sends anything other than image/* (or an HTML page for a missing file) jsPDF cannot
// read the data and fails with "Supplied Data is not a valid base64-String".
const loadAsDataUrl = (url) => fetch(url)
    .then((response) => {
        if (!response.ok) throw new Error('Unable to load the employment contract template.');
        return response.arrayBuffer();
    })
    .then((buffer) => {
        const bytes = new Uint8Array(buffer);
        // JPEG files start with FF D8 FF
        if (bytes.length < 3 || bytes[0] !== 0xFF || bytes[1] !== 0xD8 || bytes[2] !== 0xFF) {
            throw new Error('The employment contract template image was not found or is not a JPEG.');
        }
        let binary = '';
        for (let i = 0; i < bytes.length; i += 0x8000) {
            binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
        }
        return `data:image/jpeg;base64,${btoa(binary)}`;
    });

// The template page images are loaded once and reused for later contracts
let backgroundsPromise = null;
const loadBackgrounds = () => {
    if (!backgroundsPromise) {
        backgroundsPromise = Promise.all(TEMPLATE_PAGES.map(loadAsDataUrl)).catch((error) => {
            backgroundsPromise = null; // allow a retry after a failed load
            throw error;
        });
    }
    return backgroundsPromise;
};

export const generateEmploymentContractPdf = async (fields = {}) => {
    const f = fields || {};
    const backgrounds = await loadBackgrounds();
    const pdf = new jsPDF({ unit: 'pt', format: [PAGE_W, PAGE_H] });
    backgrounds.forEach((data, i) => {
        if (i > 0) pdf.addPage();
        pdf.addImage(data, 'JPEG', 0, 0, PAGE_W, PAGE_H);
    });
    const pages = backgrounds.map((_, i) => i + 1); // jsPDF page numbers

    const name = clean(f.employeeName);

    // Salary: "₹ 20,000 Monthly" (amount with thousands separators + the Salary Type)
    const rawSalary = clean(f.basicSalary);
    const salaryNumber = Number(rawSalary.replace(/,/g, ''));
    const salaryAmount = rawSalary && Number.isFinite(salaryNumber)
        ? salaryNumber.toLocaleString('en-IN', { maximumFractionDigits: 2 })
        : rawSalary;
    const salary = salaryAmount ? `₹ ${salaryAmount} ${clean(f.salaryType)}`.trim() : '';

    // Daily working hours: the stored value, or "From .. To .." when shift times exist
    const shiftFrom = clean(f.normalShiftFrom);
    const shiftTo = clean(f.normalShiftTo);
    const dailyHours = clean(f.dailyWorkHours) || (shiftFrom || shiftTo ? `From ${shiftFrom} To ${shiftTo}` : '');

    const responsibilities = (Array.isArray(f.responsibilities) ? f.responsibilities : [])
        .map((item) => {
            const responsibility = clean(item && item.responsibility);
            const kpis = clean(item && item.kpis);
            if (responsibility && kpis) return `${responsibility} (KPI: ${kpis})`;
            return responsibility || kpis;
        })
        .filter(Boolean);

    const values = {
        employeeName: name,
        fatherOrSpouseName: clean(f.fatherOrSpouseName),
        employeeAddress: clean(f.employeeAddress),
        employeeIdNumber: clean(f.employeeIdNumber),
        employeeMobileNo: clean(f.employeeMobileNo),
        emergencyContact: clean(f.emergencyContact),
        designation: clean(f.designation),
        department: clean(f.department),
        workLocation: clean(f.workLocation),
        dateOfJoining: formatDate(f.dateOfJoining),
        employmentType: clean(f.employmentType),
        probationPeriod: clean(f.probationPeriod),
        reportingTo: clean(f.reportingTo),
        salary,
        salaryType: clean(f.salaryType),
        paymentType: clean(f.paymentType),
        dailyHours,
        breakTime: clean(f.breakTime),
        weeklyOff: clean(f.weeklyOff),
        noticePeriod: clean(f.noticePeriod),
        signatureName: name ? `Name: ${name}` : '',
    };

    const whiteRect = (page, x, y, w, h) => {
        pdf.setPage(page);
        pdf.setFillColor(255, 255, 255);
        pdf.rect(x, y, w, h, 'F');
    };
    const whiteOut = (page, [x0, y0, x1, y1]) => whiteRect(page, x0 + 0.7, y0 + 0.7, x1 - x0 - 1.4, y1 - y0 - 1.4);
    const place = (page, img) => {
        if (!img) return;
        pdf.setPage(page);
        // 'FAST' compresses the pixels; jsPDF's default ('NONE') stores them raw, which is what inflated the PDF
        pdf.addImage(img.data, 'PNG', img.x, img.y, img.w, img.h, undefined, 'FAST');
    };

    for (const key of Object.keys(CELLS)) {
        const target = CELLS[key];
        const page = pages[target.page];
        const value = values[key];
        if (target.clear === 'ifValue' ? value : target.clear) whiteOut(page, target.box);
        if (!value) continue;
        place(page, textImage(value, target.box));
    }

    // Contract date blanks: the underscores are replaced by the contract date (empty when none is set)
    const contractDate = formatDate(f.contractDate);
    DATE_BLANKS.forEach((blank) => {
        whiteRect(pages[0], blank[0] - 0.7, blank[1], blank[2] - blank[0] + 1.4, blank[3] - blank[1] + 1.4);
    });
    if (contractDate) {
        const [x0, y0, x1, y1] = DATE_BLANKS[0];
        place(pages[0], textImage(contractDate, [x0 - PAD_X, y0, x1 + PAD_X, y1 + 1.4]));
    }

    // Roles & Responsibilities: the template has 5 numbered rows (split across pages 1-2). Text that does not fit a single row
    // (or items beyond five) is also listed in full on an added continuation page.
    let needContinuation = responsibilities.length > RESPONSIBILITY_ROWS.length;
    for (let i = 0; i < RESPONSIBILITY_ROWS.length && i < responsibilities.length; i++) {
        const row = RESPONSIBILITY_ROWS[i];
        const img = textImage(responsibilities[i], row.box);
        if (img && img.truncated) needContinuation = true;
        place(pages[row.page], img);
    }
    if (needContinuation) {
        pdf.addPage();
        const extra = pdf.internal.getNumberOfPages();
        const left = 46.9;
        const width = 612 - 2 * left;
        let y = 60;
        place(extra, textImage('Roles and Responsibilities (continued)', [left, y, left + width, y + 18], { bold: true }));
        y += 28;
        const probe = document.createElement('canvas').getContext('2d');
        probe.font = `${MAX_PT * SCALE}px ${FONT}`;
        for (let i = 0; i < responsibilities.length; i++) {
            const line = `${i + 1}. ${responsibilities[i]}`;
            const rows = wrap(probe, line, (width - PAD_X * 2) * SCALE).length;
            const h = Math.max(13, rows * MAX_PT * 1.15 + 3);
            if (y + h > 740) break;
            place(extra, textImage(line, [left, y, left + width, y + h]));
            y += h + 2;
        }
    }

    return pdf.output('blob');
};
